import { useMutation, useQuery } from "@tanstack/react-query";
import { Platform } from "react-native";
import { getApiUrl, fetchWithAuth, getAuthToken } from "@/lib/query-client";
import { useAuth } from "./useAuth";
import { setPendingCheckoutIntent } from "@/lib/checkoutIntent";
import * as WebBrowser from "expo-web-browser";

const domain = process.env.EXPO_PUBLIC_DOMAIN || "spectra-api-server-production.up.railway.app";
const BASE_URL = `https://${domain}`;

const RETURN_URL = `${BASE_URL}/settings`;

export interface SubscriptionStatus {
    tier:
        | "retail"
        | "professional"
        | "elite"
        | "desk"
        | "institutional"
        | "enterprise"
        | "agent_starter"
        | "agent_pro"
        | "agent_scale"
        | undefined;
    status: "active" | "trialing" | "past_due" | "canceled" | "incomplete" | "unknown";
    trialEndDate: string | null;
}

interface CheckoutRequest {
    tier: string;
    billingPeriod?: "monthly" | "annual";
    returnUrl?: string;
    beforeRedirect?: () => void | Promise<void>;
    /** Where the click came from — funnel attribution, and the source stamped
     *  on a recovered intent. Never used for logic. */
    source?: string;
}

/**
 * The server answered "sign in first" to someone the screen believed was
 * already signed in. Recoverable — the caller sends them to `Auth` and the
 * stored intent brings them back to the same purchase.
 *
 * 🔴 WHY THIS EXISTS. Every one of the three surfaces that can start a payment
 * guards on its OWN read of identity (`!user || user.isDemo`) before calling.
 * On 2026-09-02 a buyer arrived from the landing page's `agent_front_door`
 * campaign, chose Agent Starter, passed that guard — and the server answered
 * `403 demo_read_only`, whose statusText is the literal `"Forbidden"` recorded
 * in `checkout.failed`. The client's identity and the token's identity had
 * disagreed, and the buyer ate the disagreement.
 *
 * A guard on the CALLER cannot see the server's answer. So the recovery lives
 * here, at the one seam all three surfaces pass through, rather than being
 * written a fourth time.
 *
 * The cause of that disagreement is NOT diagnosed — see the note in
 * `checkoutIntent.ts`. This makes it non-fatal; it does not explain it.
 */
export class CheckoutNeedsAccountError extends Error {
    readonly needsAccount = true;
    constructor(readonly tier: string) {
        super("checkout_needs_account");
        this.name = "CheckoutNeedsAccountError";
    }
}

export function isCheckoutNeedsAccount(err: unknown): err is CheckoutNeedsAccountError {
    return !!err && (err as any).needsAccount === true;
}

export function useBilling() {
    const { user } = useAuth();
    const apiUrl = getApiUrl();

    const statusQuery = useQuery<SubscriptionStatus>({
        queryKey: ["/api/billing/status", user?.id],
        enabled: !!user?.id,
        queryFn: async () => {
            const token = getAuthToken();
            const url = `${apiUrl}/api/billing/status?userId=${user?.id}`;
            try {
                const res = await fetch(url, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (res.status === 401) {
                    // NOT "retail". A 401 means we do not know who this is — it is
                    // an absent answer, not a downgrade. This branch is why an
                    // admin on enterprise appeared as retail: any expired token,
                    // or a query that fired before getAuthToken() resolved, and
                    // the app asserted the lowest paid tier.
                    //
                    // The catch below already carries this rule ("never silently
                    // downgrade to retail on network/DB errors"); the 401 path was
                    // missed. The server is not the problem — routes.ts:4906
                    // returns enterprise for an admin email once authenticated.
                    //
                    // Consequences of getting this wrong are not cosmetic: tier
                    // drives the persona default, so a transient 401 also
                    // collapsed the dense layout to plain.
                    // "unknown" is the type's existing word for this; there is no
                    // "unauthenticated" member and inventing one would widen a
                    // union that other screens switch on.
                    return { tier: undefined, status: "unknown", trialEndDate: null } as SubscriptionStatus;
                }
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.error || "Failed to fetch subscription status");
                }
                return res.json();
            } catch {
                // F3 fix: never silently downgrade to "retail" on network/DB errors.
                // Return undefined tier so UI shows "loading" instead of upgrade prompts.
                return { tier: undefined, status: "unknown", trialEndDate: null } as SubscriptionStatus;
            }
        },
    });

    const openUrl = (url: string) => {
        if (Platform.OS === "web") {
            window.location.href = url;
        } else {
            WebBrowser.openBrowserAsync(url);
        }
    };

    const createCheckout = useMutation({
        mutationFn: async ({
            tier,
            billingPeriod = "monthly",
            returnUrl = RETURN_URL,
            beforeRedirect,
            source = "checkout",
        }: CheckoutRequest) => {
            const token = getAuthToken();
            const response = await fetchWithAuth(`${apiUrl}/api/billing/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ tier, billingPeriod, userId: user?.id, email: user?.email, returnUrl }),
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                // 🔴 The ONE recoverable failure on the purchase path. Branch on
                // the error BODY, never on the status alone: `requireNotDemo`
                // and "this tier has no Stripe price configured" both answer
                // 403, and only the first one is fixed by signing in. Storing a
                // purchase intent for the second would ambush the buyer with
                // "Continue to Agent Starter" fifteen minutes after they were
                // never able to buy it.
                if (errData?.error === "demo_read_only") {
                    setPendingCheckoutIntent(tier, billingPeriod, source);
                    throw new CheckoutNeedsAccountError(tier);
                }
                throw new Error(errData.error || "Failed to create checkout session");
            }
            const { url } = await response.json();
            try {
                await beforeRedirect?.();
            } catch {
                // Measurement must never prevent a buyer from reaching Stripe.
            }
            return url;
        },
        onSuccess: (url) => {
            openUrl(url);
        },
    });

    const openPortal = useMutation({
        mutationFn: async () => {
            const token = getAuthToken();
            const response = await fetchWithAuth(`${apiUrl}/api/billing/portal`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ userId: user?.id, returnUrl: RETURN_URL }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || "Failed to create portal session");
            }
            const { url } = await response.json();
            return url;
        },
        onSuccess: (url) => {
            openUrl(url);
        },
    });

    return {
        subscription: statusQuery.data,
        isLoading: statusQuery.isLoading,
        refetch: statusQuery.refetch,
        createCheckout,
        openPortal,
    };
}
